import * as Sentry from '@sentry/react'

import type { LoggerClient, LoggerContext } from 'clients/logger/interface'
import { isCancellation } from 'clients/logger/is-cancellation'
import { normalizeException } from 'clients/logger/normalize-exception'

type ErrorOptions = {
  error?: unknown
  operation?: string
  context?: LoggerContext
}

export class SentryLogger implements LoggerClient {
  private readonly enabled: boolean

  constructor(enabled = Boolean(import.meta.env?.VITE_SENTRY_DSN)) {
    this.enabled = enabled
  }

  info(message: string, context?: LoggerContext): void {
    if (!this.enabled) return

    Sentry.addBreadcrumb({
      level: 'info',
      category: 'app.logger',
      message,
      data: context,
    })
  }

  error(message: string, options?: ErrorOptions): void {
    if (!this.enabled || isCancellation(options?.error)) return

    Sentry.withScope((scope) => {
      scope.setLevel('error')
      scope.setTag('logger', 'sentry')
      scope.setTag('error_source', 'handled')
      if (options?.operation) {
        scope.setTag('operation', options.operation)
        scope.setFingerprint(['{{ default }}', options.operation])
      }
      scope.setContext('logger', {
        message,
        ...options?.context,
      })

      if (options?.error !== undefined) {
        Sentry.captureException(normalizeException(options.error))
        return
      }

      Sentry.captureMessage(message, 'error')
    })
  }
}
